import { ChipProps } from '@mui/material';
import moment from 'moment';

import {
    ChannelSubscriptionState,
    ChannelSubscriptionType,
} from '../../../../shared/types';
import {
    AllSubscriptionFilterValue,
    AdminUserChannelSubscription,
    AdminUserChannelSubscriptionRow,
    InactiveSubscriptionFilterValue,
    UserChannelSubscriptionFilters,
} from './types';

export const ALL_SUBSCRIPTION_FILTER_VALUE: AllSubscriptionFilterValue = 'all';
export const INACTIVE_SUBSCRIPTION_FILTER_VALUE: InactiveSubscriptionFilterValue =
    'inactive';

export const statusDetails: Record<
    ChannelSubscriptionState,
    { label: string; color: ChipProps['color'] }
> = {
    active: { label: 'ACTIVE', color: 'success' },
    pending: { label: 'INACTIVE', color: 'default' },
    canceled: { label: 'INACTIVE', color: 'default' },
    expired: { label: 'INACTIVE', color: 'default' },
    paymentFailed: { label: 'INACTIVE', color: 'default' },
    rejected: { label: 'INACTIVE', color: 'default' },
};

export const subscriptionTypeLabels: Record<ChannelSubscriptionType, string> = {
    [ChannelSubscriptionType.Account]: 'Account',
    [ChannelSubscriptionType.Stripe]: 'Paid',
    [ChannelSubscriptionType.External]: 'External',
    [ChannelSubscriptionType.NonCharge]: 'Non-charge',
    [ChannelSubscriptionType.Trial]: 'Trial',
    [ChannelSubscriptionType.Free]: 'Free',
};

export const formatStatusDate = (date?: Date): string | undefined =>
    date && moment(date).isValid()
        ? moment(date).format('DD MMM YYYY [at] HH:mm')
        : undefined;

export const getStatusUpdatedAt = (
    subscription: AdminUserChannelSubscription
): Date | undefined => {
    switch (subscription.subscriptionState) {
        case 'active':
            return subscription.cancelRequestedAt || subscription.createdAt;
        case 'canceled':
            return subscription.cancelRequestedAt || subscription.updatedAt;
        case 'expired':
            return subscription.cancelAt || subscription.updatedAt;
        case 'pending':
            return subscription.createdAt;
        case 'rejected':
        case 'paymentFailed':
            return subscription.updatedAt;
    }
};

export const toSubscriptionRows = (
    subscriptions: AdminUserChannelSubscription[]
): AdminUserChannelSubscriptionRow[] =>
    subscriptions.map((subscription) => ({
        ...subscription,
        updated: getStatusUpdatedAt(subscription),
    }));

const getDisplayedStatusOrder = (
    subscription: AdminUserChannelSubscriptionRow
): number => (subscription.subscriptionState === 'active' ? 0 : 1);

const matchesDisplayedStatusFilter = (
    subscription: AdminUserChannelSubscriptionRow,
    { subscriptionState }: UserChannelSubscriptionFilters
) => {
    const isActiveSubscription = subscription.subscriptionState === 'active';

    if (subscriptionState === ALL_SUBSCRIPTION_FILTER_VALUE) {
        return true;
    }

    return subscriptionState === 'active'
        ? isActiveSubscription
        : !isActiveSubscription;
};

const matchesSubscriptionTypeFilter = (
    subscription: AdminUserChannelSubscriptionRow,
    { subscriptionType }: UserChannelSubscriptionFilters
): boolean =>
    subscriptionType === ALL_SUBSCRIPTION_FILTER_VALUE ||
    subscription.subscriptionType === subscriptionType;

export const sortSubscriptions = (
    subscriptions: AdminUserChannelSubscriptionRow[]
): AdminUserChannelSubscriptionRow[] =>
    [...subscriptions].sort(
        (first, second) =>
            getDisplayedStatusOrder(first) - getDisplayedStatusOrder(second) ||
            moment(second.updated).valueOf() -
                moment(first.updated).valueOf() ||
            first.channel.name.localeCompare(second.channel.name)
    );

export const filterSubscriptions = (
    subscriptions: AdminUserChannelSubscriptionRow[],
    filters: UserChannelSubscriptionFilters
): AdminUserChannelSubscriptionRow[] =>
    subscriptions.filter(
        (subscription) =>
            matchesDisplayedStatusFilter(subscription, filters) &&
            matchesSubscriptionTypeFilter(subscription, filters)
    );
